<template>
    <PtHeaderDropdown
        ref="transDropdown"
        class="lang-switcher th-dropdown th-dropdown-end th-dropdown-open"
        trigger="click"
        :class="{
            'pa-pointer-events-none': disabled,
            'pa-cursor-not-allowed': disabled,
        }"
    >
        <template #reference="{ open }">
            <ElasticButton
                :alwaysNeedTooltip="true"
                :disabled="disabled"
            >
                <template #tipContent>
                    <div v-html="$t('translate-tip')" />
                </template>
                <template #icon>
                    <PtIcon :icon="translateIconName" />
                </template>
                <template #default>
                    <span class="translate-dropdown-title">{{ translationOptionsService.getOptionLabel(rootLang) }}</span>
                    <PtIcon v-if="open" icon="SolidDropdownClose" />
                    <PtIcon v-else icon="SolidDropdownOpen" />
                </template>
            </ElasticButton>
        </template>
        <template #popper="{ close }">
            <ul class="th-dropdown-content th-menu th-dropdown-content__shadow pa-mt-1 pa-w-44">
                <template v-for="menuItem in langSwitchOptions">
                    <li
                        v-if="!menuItem.lang"
                        :key="menuItem.label"
                        class="lang-switcher-group-title !pa-mb-0 !pa-text-xs !pa-leading-4"
                    >
                        <span>
                            {{ menuItem.label }}
                        </span>
                    </li>
                    <li
                        v-else
                        :key="menuItem.lang"
                        :class="`pendo-track__function-bar__translate-${menuItem.lang}`"
                        class="pa-flex pa-justify-between"
                        @click="() => setTransLang(menuItem.lang, menuItem.langType, close)"
                    >
                        <span>{{ menuItem.label }}</span>
                        <ElPopover
                            v-if="menuItem.tip"
                            placement="bottom"
                            trigger="hover"
                            :width="500"
                            :visibleArrow="false"
                        >
                            <template #reference>
                                <PtIcon
                                    icon="SolidInfo"
                                    class="!pa-fill-grey-60"
                                />
                            </template>
                            <span v-html="menuItem.tip" />
                        </ElPopover>
                    </li>
                </template>
            </ul>
        </template>
    </PtHeaderDropdown>
</template>

<script setup lang="ts">
import '@patsnap-ui/icon/assets/solid/DropdownOpen.svg';
import '@patsnap-ui/icon/assets/solid/TranslateChinese.svg';
import '@patsnap-ui/icon/assets/solid/TranslateEnglish.svg';
import '@patsnap-ui/icon/assets/solid/info.svg';
import { useI18n } from '@/composables/use-i18n';
import { useTranslationStates } from '@/composables/use-translation-states';
import type { PatentData } from '@/services/patent-service.interface';
import type { Side } from '@/services/translation/translate-service.interface';
import type { Language } from '@/domains/translation/types';
import { LangTypeMode } from '@/domains/translation/enums';
import fileWrapperMf from '@/subview_plugins/FireWrapper/manifest';
import customsMf from '@/subview_plugins/LegalCustoms/manifest';
import rerexamMf from '@/subview_plugins/LegalRerexam/manifest';
import legalStatusMf from '@/subview_plugins/LegalStatus/manifest';
import licensingMf from '@/subview_plugins/Licensing/manifest';
import litigationMf from '@/subview_plugins/Litigation/manifest';
import imageMf from '@/subview_plugins/Image/manifest';
import literatureMf from '@/subview_plugins/Literature/manifest';
import pdfMf from '@/subview_plugins/Pdf/manifest';
import pledgeMf from '@/subview_plugins/Pledge/manifest';
import valuationMf from '@/subview_plugins/valuation/manifest';
import { Popover as ElPopover } from 'element-ui';
import _ from 'lodash';
import { HeaderDropdown as PtHeaderDropdown, Icon as PtIcon } from 'patsnap-biz';
import { computed, ref, watch } from 'vue';
import { useRoute } from 'vue-router/composables';
import ElasticButton from '../ElasticButton.vue';
import { useCompareUrlState } from '@/composables/use-compare-url-state';
import { injectService } from '@patsnap/tahiti-core/ioc/inject';
import { TranslationOptionsService } from '@/domains/translation/services/translation-options-service';
import type { TranslationOptions } from '@/domains/translation/types';
import { DEFAULT_LANGUAGE } from '@/domains/translation/constants';
import { useDisposes } from '@/composables/use-disposes';

const SIDE_IDENTIFIER: Side = 'both';

const EXCLUDE_TRANSLATE_LIST = new Set([
    valuationMf.name,
    legalStatusMf.name,
    litigationMf.name,
    rerexamMf.name,
    licensingMf.name,
    pledgeMf.name,
    fileWrapperMf.name,
    customsMf.name,
    pdfMf.name,
    imageMf.name,
    literatureMf.name,
]);

const { disposes } = useDisposes();

const translationOptionsService = injectService<TranslationOptionsService>(TranslationOptionsService);

const props = withDefaults(defineProps<{
    patentId: string;
    patent: Partial<PatentData>;
    side?: Side;
}>(), {
    side: 'left',
});

const { isCompareView } = useCompareUrlState();

const route = useRoute();

const { locale } = useI18n();

const {
    currentTranslateLanguages,
    currentLanguageModes,
    userPreferredLangOptions,
    switchTranslateLanguage,
    setUserPreferredLangOptions,
} = useTranslationStates();

// 从vuex获取状态
const rootLang = computed(() => currentTranslateLanguages.value[props.side || 'left']);
const rootLangType = computed(() => currentLanguageModes.value[props.side || 'left']);
const userPreferredLangOption = computed<{ lang: string; langType: LangTypeMode; }>(() => userPreferredLangOptions.value[props.side || 'left']);

// 部分单视图不支持翻译，需禁用
const disabled = computed(() => EXCLUDE_TRANSLATE_LIST.has(route.meta?.pluginName));

// 翻译图标，需要区分中英文
const translateIconName = computed(() => {
    return locale.value.toUpperCase() === 'EN'
        ? 'SolidTranslateChinese'
        : 'SolidTranslateEnglish';
});

// 计算专利当前语言
function getPatentLanguage(patent: Partial<PatentData>): string {
    return patent.ABST_LANG || patent.TITLE_LANG || '';
}

// 设置可用翻译语言的配置
const langSwitchOptions = ref<TranslationOptions>(translationOptionsService.getModelData());
disposes.push(
    translationOptionsService.onStatesChange(states => {
        langSwitchOptions.value = states;
    }),
);

// 切换专利需要更新对应的翻译配置
watch(() => props.patent, (data) => {
    translationOptionsService.initialize({
        isCompareView: isCompareView.value,
        patentLang: getPatentLanguage(data),
        patentOriginLang: data.LANGS || [],
    });
}, { immediate: true });

// 判断当前翻译和用户点击时候的翻译是否一致
function getUserPreferIsDiffer() {
    const isLangDiffer = rootLang.value !== userPreferredLangOption.value.lang;
    const isLangTypeDiffer = rootLangType.value !== userPreferredLangOption.value.langType;
    return isLangDiffer && isLangTypeDiffer;
}

// 获取可用的原文语言
function getAvailableOrigins(value: TranslationOptions) {
    return value
        .filter(option => option.langType === LangTypeMode.ORIGINAL)
        .map(option => option.lang);
}

// 原文语言不同专利可能不同，因此如果预期翻译的原文语言不在当前的可用的原文语言列表中，需要重置为默认
watch(langSwitchOptions, (value: TranslationOptions) => {
    if (_.isEmpty(props.patent)) return;

    const availableOrigins = getAvailableOrigins(value);

    // 判断是否为使用原文
    const isUserPreferredOriginTrans = userPreferredLangOption.value.langType === LangTypeMode.ORIGINAL;
    if (!isUserPreferredOriginTrans) return;

    // 检查目标原文语言是否可用
    const isUserPreferredOriginTransAvailable = availableOrigins.includes(userPreferredLangOption.value.lang);
    if (!isUserPreferredOriginTransAvailable) {
        switchTranslateLanguage({
            side: SIDE_IDENTIFIER,
            lang: DEFAULT_LANGUAGE,
            langType: LangTypeMode.DEFAULT,
        });
    } else {
        const userPreferIsDiffer = getUserPreferIsDiffer();
        if (!userPreferIsDiffer) return;

        switchTranslateLanguage({
            side: SIDE_IDENTIFIER,
            lang: userPreferredLangOption.value.lang,
            langType: userPreferredLangOption.value.langType,
        });
    }
});

// 切换翻译
function setTransLang(lang: Language, langType: LangTypeMode, close: () => void) {
    const targetLang = { side: SIDE_IDENTIFIER, lang, langType };
    switchTranslateLanguage(targetLang);
    setUserPreferredLangOptions(targetLang);

    close();

    window.rvTrack('Translate Toggle', { transLang: lang });
}
</script>

<style lang="scss">
.lang-switcher-group-title {
    @apply th-menu-item-disabled pa-border-t-[1px] pa-border-grey-60 pa-p-2 pa-mx-[-8px] pa-w-[calc(100%+16px)] pa-pointer-events-none;
}
</style>
